/**
 * What the laptop Agenda shows, shaped from the lanes' own data. Pure.
 *
 * The Agenda (2026-10-30, round-2 mockup `AgendaSchedules`) is the phone's
 * Today tab drawn as a table for a laptop, over more than one day: the
 * same items (`useCalendarLaneData`: the same three sources, automatic
 * work left out), filed by the same rules (`buildAgenda` folds today's
 * finished work and places the now line, `withBursts` groups a robot's
 * deliveries, `groupMembership` files items under the robot-group chips).
 * Nothing here re-decides what is on the calendar; it only cuts the range
 * into days and words each day's header.
 *
 * ONE DIFFERENCE FROM THE PHONE, ON PURPOSE. The phone keeps a failure in
 * view for half an hour and then folds it into "Earlier today". The
 * Agenda never folds one away: today's earlier rows that didn't finish
 * stay listed under the "Earlier today" line (which still counts them),
 * red, where a supervisor scanning the day sees them.
 */
import type { ScheduleOccurrence } from '../../../api/v1';
import { paintFor, type LanePaint } from '../calendar-categories';
import { DIDNT_FINISH, robotKeyOf, type LaneItem } from './lane-items';
import { robotMatches, type RobotEntry } from './lane-layout';
import {
  addDays,
  dateKeyOf,
  dayWindow,
  WEEKDAY_LONG,
  weekdayOf,
  type DateKey,
  type TimeWindow,
} from './lane-time';
import {
  buildAgenda,
  isUpcoming,
  rowStartMs,
  whereOf,
  withBursts,
  type AgendaRow,
  type ScheduleRow,
} from './phone-model';
import type { SiteBar } from './site-row';

const MIN = 60_000;

/** "Run next" lists this many runs. */
export const NEXT_RUNS_SHOWN = 5;

// ---- the range ------------------------------------------------------------------

/** The days the Agenda shows: `from` and the `days - 1` after it. */
export function agendaDays(from: DateKey, days: number): DateKey[] {
  return Array.from({ length: Math.max(1, days) }, (_, i) => addDays(from, i));
}

/** The whole range, on the site's clock: what is fetched for it. */
export function agendaWindow(from: DateKey, days: number, timeZone: string): TimeWindow {
  const keys = agendaDays(from, days);
  return {
    fromMs: dayWindow(keys[0]!, timeZone).fromMs,
    toMs: dayWindow(keys[keys.length - 1]!, timeZone).toMs,
  };
}

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** "Friday 30 Oct". */
export function longDayLabel(key: DateKey): string {
  const [, m, d] = key.split('-').map(Number) as [number, number, number];
  return `${WEEKDAY_LONG[weekdayOf(key)]} ${d} ${MONTH_SHORT[m - 1]}`;
}

/** A day's header: "Today · Friday 30 Oct", "Tomorrow · …", "Yesterday ·
 *  …", else the day alone. */
export function agendaDayLabel(key: DateKey, todayKey: DateKey): string {
  const day = longDayLabel(key);
  if (key === todayKey) return `Today · ${day}`;
  if (key === addDays(todayKey, 1)) return `Tomorrow · ${day}`;
  if (key === addDays(todayKey, -1)) return `Yesterday · ${day}`;
  return day;
}

// ---- filters --------------------------------------------------------------------

/**
 * The toolbar's "Name or floor" box, for rows: the robot (its name,
 * label, fleet or floor -- the lanes' own `robotMatches`), or the place
 * or schedule the row names.
 */
export function agendaItemMatches(
  item: LaneItem,
  query: string,
  robots: ReadonlyMap<string, RobotEntry>,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const robot = item.robotKey ? robots.get(item.robotKey) : undefined;
  if (robot && robotMatches(robot, q)) return true;
  return [item.robotName ?? '', whereOf(item) ?? '', item.title].some((t) =>
    t.toLowerCase().includes(q),
  );
}

/** "Needs a look": work that didn't finish. */
export function needsALook(item: LaneItem): boolean {
  return DIDNT_FINISH.has(item.outcome);
}

// ---- days -----------------------------------------------------------------------

export interface DayCounts {
  tasks: number;
  didntFinish: number;
  toRun: number;
}

export interface AgendaDay {
  key: DateKey;
  label: string;
  isToday: boolean;
  counts: DayCounts;
  /** Today only, while folding: finished work behind one line. */
  earlier: AgendaRow[];
  earlierCount: { done: number; didntFinish: number; other: number };
  /** Of `earlier`, the rows kept in view while it is folded: the ones
   *  with work that didn't finish. */
  pinned: AgendaRow[];
  rows: AgendaRow[];
  /** Today only: the now line goes before `rows[nowIndex]`. */
  nowIndex: number | null;
}

export function rowHasFailure(r: AgendaRow): boolean {
  return r.kind === 'item'
    ? DIDNT_FINISH.has(r.item.outcome)
    : r.items.some((it) => DIDNT_FINISH.has(it.outcome));
}

export function rowItems(r: AgendaRow): LaneItem[] {
  return r.kind === 'item' ? [r.item] : r.items;
}

function byStart(a: LaneItem, b: LaneItem): number {
  return a.startMs - b.startMs || a.endMs - b.endMs || a.id.localeCompare(b.id);
}

/**
 * The range cut into days. Each item is filed under the day it started
 * (on the site's clock); one already running when the range opens is
 * filed under its first day. Today folds its finished work (`buildAgenda`)
 * unless `fold` is off -- "Needs a look" lists only failures, and
 * folding those would hide the list it asked for.
 */
export function buildAgendaDays(args: {
  items: readonly LaneItem[];
  days: readonly DateKey[];
  todayKey: DateKey;
  nowMs: number;
  timeZone: string;
  fold?: boolean;
}): AgendaDay[] {
  const { days, todayKey, nowMs, timeZone } = args;
  const fold = args.fold ?? true;
  const first = days[0];
  const perDay = new Map<DateKey, LaneItem[]>(days.map((k) => [k, []]));
  for (const it of args.items) {
    if (!Number.isFinite(it.startMs)) continue;
    let key = dateKeyOf(it.startMs, timeZone);
    if (first !== undefined && key < first) key = first;
    perDay.get(key)?.push(it);
  }
  return days.map((key) => {
    const items = (perDay.get(key) ?? []).sort(byStart);
    const counts: DayCounts = {
      tasks: items.length,
      didntFinish: items.filter((it) => DIDNT_FINISH.has(it.outcome)).length,
      toRun: items.filter((it) => isUpcoming(it.outcome)).length,
    };
    const isToday = key === todayKey;
    const base = {
      key,
      label: agendaDayLabel(key, todayKey),
      isToday,
      counts,
    };
    if (isToday && fold) {
      const m = buildAgenda(items, nowMs);
      return {
        ...base,
        earlier: m.earlier,
        // ONE "didn't finish" count per day (`DIDNT_FINISH`: failed or
        // cancelled, as the Day view and the phone count it). The fold
        // folds none of them away -- old ones stay listed under it, the
        // last half hour's stay in the list -- so its line counts them
        // all, and says what the day's header says.
        earlierCount: { ...m.earlierCount, didntFinish: counts.didntFinish },
        pinned: m.earlier.filter(rowHasFailure),
        rows: m.rows,
        nowIndex: m.nowIndex,
      };
    }
    const rows = withBursts(items);
    let nowIndex: number | null = null;
    if (isToday) {
      nowIndex = rows.findIndex((r) => rowStartMs(r) >= nowMs);
      if (nowIndex === -1) nowIndex = rows.length;
    }
    return {
      ...base,
      earlier: [],
      earlierCount: { done: 0, didntFinish: 0, other: 0 },
      pinned: [],
      rows,
      nowIndex,
    };
  });
}

/** "164 tasks · 3 didn’t finish · 81 still to run". */
export function daySummary(c: DayCounts): string {
  if (c.tasks === 0) return 'Nothing on the calendar';
  if (c.toRun === c.tasks) return `${c.tasks} still to run`;
  const parts = [c.tasks === 1 ? '1 task' : `${c.tasks} tasks`];
  if (c.didntFinish) parts.push(`${c.didntFinish} didn’t finish`);
  if (c.toRun) parts.push(`${c.toRun} still to run`);
  return parts.join(' · ');
}

/** "Earlier today: 92 done · 3 didn’t finish". */
export function earlierText(c: AgendaDay['earlierCount']): string {
  const parts = [`${c.done} done`];
  if (c.didntFinish) parts.push(`${c.didntFinish} didn’t finish`);
  if (c.other) parts.push(`${c.other} other`);
  return `Earlier today: ${parts.join(' · ')}`;
}

// ---- schedules ------------------------------------------------------------------

/** The selection id of a schedule card (task items have their own ids). */
export function scheduleSelectionId(scheduleId: string): string {
  return `schedule:${scheduleId}`;
}

/**
 * Where a schedule's next run is looked for: the Agenda's own range when
 * it covers now (nothing more is fetched), else today and tomorrow -- the
 * phone's window -- so "Next" never names a run in the past or skips one
 * that is coming because the reader paged away.
 */
export function nextRunWindow(
  shown: TimeWindow,
  nowMs: number,
  todayKey: DateKey,
  timeZone: string,
): { window: TimeWindow; ownFetch: boolean } {
  if (shown.fromMs <= nowMs && nowMs < shown.toMs) return { window: shown, ownFetch: false };
  return { window: agendaWindow(todayKey, 2, timeZone), ownFetch: true };
}

/** A schedule's paint: its type, or a linen run for a delivery pinned to
 *  a linen robot (`paintFor`, the rule its runs are painted by). */
export function schedulePaint(row: ScheduleRow, linen: ReadonlySet<string>): LanePaint {
  const task = row.schedule.task;
  return paintFor(row.lane, robotKeyOf(task?.fleet, task?.robot), linen);
}

/** Whole days from today to the end of `window`: the N in "Not in the
 *  next N days". */
export function horizonDays(window: TimeWindow, todayKey: DateKey, timeZone: string): number {
  const last = dateKeyOf(window.toMs - 1, timeZone);
  let n = 1;
  let k = todayKey;
  while (k < last && n < 60) {
    k = addDays(k, 1);
    n += 1;
  }
  return n;
}

/** "Not in the next 2 days" -- what a schedule with no run in the
 *  fetched window says instead of a time. */
export function noRunText(days: number): string {
  return days <= 1 ? 'Not today' : `Not in the next ${days} days`;
}

/** A schedule's runs still to come in the fetched window, soonest first. */
export function upcomingRuns(
  scheduleId: string,
  occurrences: readonly ScheduleOccurrence[],
  nowMs: number,
  limit = NEXT_RUNS_SHOWN,
): ScheduleOccurrence[] {
  return occurrences
    .filter((o) => o.schedule_id === scheduleId && !o.status && o.start_ms >= nowMs - MIN)
    .sort((a, b) => a.start_ms - b.start_ms || a.original_start_ms - b.original_start_ms)
    .slice(0, limit);
}

// ---- blocked times --------------------------------------------------------------

/** Maintenance windows and lift rules in the range not over yet, soonest first. */
export function blockedTimes(bars: readonly SiteBar[], nowMs: number): SiteBar[] {
  return bars
    .filter((b) => (b.kind === 'maintenance' || b.kind === 'lift') && b.endMs > nowMs)
    .sort((a, b) => a.startMs - b.startMs || a.id.localeCompare(b.id));
}
